/**
 * Pure H OS · Pre-Production Gap Closure · Weekly Digest.
 *
 * A single Monday-morning executive digest composed from live modules:
 *   · Revenue + burn (from Products Hub + Cost Centers)
 *   · Ship / Attention count (from Executive Inbox sources)
 *   · Top approved / deferred decisions (last 7d)
 *   · Notable meetings scheduled this week
 *   · Highest-severity open risks
 *   · Milestones due this week (PMO)
 *   · AI recommendation queue depth (open insights)
 *
 * This page is what "Weekly Digest" report links used to reference.
 */
import { useMemo } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { DocumentActions } from "../components/DocumentActions";
import { PRODUCTS_HUB } from "../mocks/productsHubData";
import { COST_CENTERS } from "../mocks/costCentersData";
import { DECISIONS } from "../mocks/decisionsData";
import { MEETINGS } from "../mocks/meetingsData";
import { RISKS_REGISTRY } from "../mocks/risksRegistryData";
import { PMO_PROJECTS } from "../mocks/pmoData";
import { AI_INSIGHTS } from "../mocks/aiData";
import { REPORTS } from "../mocks/reportsData";
import { Link } from "react-router-dom";

export default function WeeklyDigest() {
  const digest = useMemo(() => {
    const revenue = PRODUCTS_HUB.reduce((s, p) => s + p.monthlyRevenueEGP, 0);
    const burn = COST_CENTERS.reduce((s, c) => s + c.spentEGP, 0);
    const recentDecisions = DECISIONS.slice(0, 6);
    const meetingsThisWeek = MEETINGS.filter((m) => /today|tomorrow|this week/i.test(m.scheduledRelative));
    const highRisks = RISKS_REGISTRY.filter((r) => r.severity === "high" && r.status === "open").slice(0, 5);
    const dueMilestones = PMO_PROJECTS
      .filter((p) => p.milestonesDone < p.milestones)
      .slice(0, 6)
      .map((p) => ({
        id: p.id,
        title: `${p.name} — ${p.milestonesDone}/${p.milestones} milestones`,
        status: p.phase,
        dueRelative: p.dueRelative,
      }));
    const aiOpen = AI_INSIGHTS.filter((i) => i.severity === "high" || i.severity === "medium").slice(0, 5);
    const anchorReport = REPORTS.find((r) => r.cadence === "weekly");
    return { revenue, burn, recentDecisions, meetingsThisWeek, highRisks, dueMilestones, aiOpen, anchorReport };
  }, []);

  return (
    <div className="space-y-6" data-testid="weekly-digest-page">
      <Card testId="wd-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <SectionHeader eyebrow="Weekly Executive Digest" title={digest.anchorReport?.title ?? "Monday · executive digest"} />
          <DocumentActions docKey="weekly-digest" testId="wd-doc-actions" />
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
          Composed live from Products · Cost Centers · Decisions · Meetings · Risks · PMO · AI · Reports.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Revenue (mo)</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {Math.round(digest.revenue).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Burn (mo)</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {Math.round(digest.burn).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Net</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: digest.revenue - digest.burn >= 0 ? "var(--ph-accent)" : "var(--ph-danger)" }}>
              {digest.revenue - digest.burn >= 0 ? "+" : "−"}EGP {Math.abs(Math.round(digest.revenue - digest.burn)).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>AI queue</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{digest.aiOpen.length} open</div>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card testId="wd-decisions">
          <SectionHeader eyebrow="Recent decisions" title={`${digest.recentDecisions.length} strategic calls`} />
          <ul className="mt-3 space-y-2">
            {digest.recentDecisions.map((d) => (
              <li key={d.id} className="text-sm flex items-center justify-between gap-3" data-testid={`wd-dec-${d.id}`}>
                <div>
                  <div style={{ color: "var(--ph-text)" }}>{d.title}</div>
                  <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{d.module} · {d.decidedBy} · {d.dateRelative}</div>
                </div>
                <SeverityBadge severity={d.severity} />
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs">
            <Link to="/pureh/decisions" style={{ color: "var(--ph-accent)" }}>Open Decisions →</Link>
          </div>
        </Card>

        <Card testId="wd-meetings">
          <SectionHeader eyebrow="Meetings this week" title={`${digest.meetingsThisWeek.length} scheduled`} />
          <ul className="mt-3 space-y-2">
            {digest.meetingsThisWeek.length === 0
              ? <li className="text-xs" style={{ color: "var(--ph-muted)" }}>None scheduled.</li>
              : digest.meetingsThisWeek.map((m) => (
                <li key={m.id} className="text-sm" data-testid={`wd-mtg-${m.id}`}>
                  <div style={{ color: "var(--ph-text)" }}>{m.title}</div>
                  <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{m.scheduledRelative} · {m.attendees.length} attendees · {m.owner}</div>
                </li>
              ))}
          </ul>
          <div className="mt-3 text-xs">
            <Link to="/pureh/meetings" style={{ color: "var(--ph-accent)" }}>Open Meetings →</Link>
          </div>
        </Card>

        <Card testId="wd-risks">
          <SectionHeader eyebrow="High-severity open risks" title={`${digest.highRisks.length} to watch`} />
          <ul className="mt-3 space-y-2">
            {digest.highRisks.length === 0
              ? <li className="text-xs" style={{ color: "var(--ph-muted)" }}>All clear.</li>
              : digest.highRisks.map((r) => (
                <li key={r.id} className="text-sm flex items-center justify-between gap-3" data-testid={`wd-risk-${r.id}`}>
                  <div>
                    <div style={{ color: "var(--ph-text)" }}>{r.title}</div>
                    <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{r.module} · {r.owner} · {r.updatedRelative}</div>
                  </div>
                  <SeverityBadge severity="high" />
                </li>
              ))}
          </ul>
          <div className="mt-3 text-xs">
            <Link to="/pureh/risks" style={{ color: "var(--ph-accent)" }}>Open Risk Register →</Link>
          </div>
        </Card>

        <Card testId="wd-milestones">
          <SectionHeader eyebrow="Upcoming milestones" title={`${digest.dueMilestones.length} due`} />
          <ul className="mt-3 space-y-2">
            {digest.dueMilestones.length === 0
              ? <li className="text-xs" style={{ color: "var(--ph-muted)" }}>None due this week.</li>
              : digest.dueMilestones.map((m) => (
                <li key={m.id} className="text-sm" data-testid={`wd-milestone-${m.id}`}>
                  <div style={{ color: "var(--ph-text)" }}>{m.title}</div>
                  <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{m.status} · due {m.dueRelative}</div>
                </li>
              ))}
          </ul>
          <div className="mt-3 text-xs">
            <Link to="/pureh/pmo" style={{ color: "var(--ph-accent)" }}>Open PMO →</Link>
          </div>
        </Card>

        <Card testId="wd-ai">
          <SectionHeader eyebrow="AI recommendations awaiting review" title={`${digest.aiOpen.length} of high/medium impact`} />
          <ul className="mt-3 space-y-2">
            {digest.aiOpen.map((a) => (
              <li key={a.id} className="text-sm flex items-center justify-between gap-3" data-testid={`wd-ai-${a.id}`}>
                <div>
                  <div style={{ color: "var(--ph-text)" }}>{a.title}</div>
                  <div className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{a.detail}</div>
                </div>
                <SeverityBadge severity={a.severity === "high" ? "high" : "medium"} />
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs">
            <Link to="/pureh/inbox" style={{ color: "var(--ph-accent)" }}>Triage in Executive Inbox →</Link>
          </div>
        </Card>

        <Card testId="wd-anchor-report">
          <SectionHeader eyebrow="Source cadence report" title={digest.anchorReport?.title ?? "Weekly report"} />
          {digest.anchorReport ? (
            <>
              <div className="mt-3 text-sm" style={{ color: "var(--ph-text)" }}>{digest.anchorReport.coverage}</div>
              {digest.anchorReport.body && (
                <div className="mt-3 text-sm whitespace-pre-wrap pt-3" style={{ color: "var(--ph-text)", borderTop: "1px solid var(--ph-border-subtle)" }}>
                  {digest.anchorReport.body}
                </div>
              )}
              <div className="mt-3 text-xs">
                <Link to="/pureh/reports" style={{ color: "var(--ph-accent)" }}>All reports →</Link>
              </div>
            </>
          ) : (
            <div className="text-xs mt-2" style={{ color: "var(--ph-muted)" }}>No weekly report registered.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
